'use client';

import { createContext, useContext, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { isGuestPath } from '@/lib/auth/guest-paths';
import { useCurrentUser } from './profile';

/**
 * The signed-in user's id, published once at the app shell — mirrors
 * `CompanyDateFormatProvider` (`src/lib/utils/format-date.tsx`): reading it
 * via `useCurrentUser()` directly inside a shared component like `Table`
 * would force every table instance, including developer-handbook demos and
 * unit tests, to carry a `QueryClient` and a stubbed `/profile` fetch.
 * Instead the shell fetches it once and publishes a plain value below.
 *
 * `undefined` outside the provider, before the profile resolves, or on a
 * guest path (there's no session to fetch a profile for) — every consumer
 * already treats "no user yet" as "don't persist/restore anything".
 */
const CurrentUserIdContext = createContext<string | undefined>(undefined);

export function CurrentUserIdProvider({ children }: Readonly<{ children: ReactNode }>) {
  const pathname = usePathname();
  const { data } = useCurrentUser({ enabled: !isGuestPath(pathname) });

  return (
    <CurrentUserIdContext.Provider value={data?.user?.id}>{children}</CurrentUserIdContext.Provider>
  );
}

export function useCurrentUserId(): string | undefined {
  return useContext(CurrentUserIdContext);
}
